import { LockIcon } from '@commenda/icons';
import { cn } from '@commenda/utils';
import Link from 'next/link';
import React from 'react';
import type { IconType } from 'react-icons';

interface SidebarLinkLegacyProps {
  label: string;
  href: string;
  icon: IconType | React.ElementType;
  isActive: boolean;
  disabled?: boolean;
  isBeta?: boolean;
}

const SidebarLegacy = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <aside
      className={cn(
        'h-screen flex flex-col border-r-2 border-r-stroke-2 w-64',
        className
      )}
    >
      {children}
    </aside>
  );
};

const SidebarHeaderLegacy = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'flex items-center py-6 px-4 gap-2 border-b border-stroke-3',
        className
      )}
    >
      {children}
    </div>
  );
};

const SidebarBodyLegacy = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        'mt-2 flex flex-col gap-4 divide-y divide-y-stroke-3 px-4 flex-1',
        className
      )}
    >
      {children}
    </div>
  );
};

const SidebarLinksLegacy = ({ links }: { links: SidebarLinkLegacyProps[] }) => {
  return (
    <div className="flex flex-col gap-2 flex-1">
      {links.map((item, idx) => {
        return (
          <Link href={item.href} key={item.label}>
            <a
              className={cn(
                'py-3 flex gap-2 items-center cursor-pointer group transition-colors px-2 rounded ',
                item.isActive ? 'bg-violet-3' : '',
                item.disabled
                  ? 'pointer-events-none bg-neutral-6'
                  : 'hover:bg-violet-3'
              )}
            >
              <div className="h-6 w-6 rounded flex justify-center items-center">
                <item.icon
                  className={cn(
                    'h-6 w-6 group-hover:text-violet-1 group:hover bg-violet-3 bg-transparent',
                    item.isActive ? 'text-violet-1' : 'text-neutral-4'
                  )}
                />
              </div>
              <div
                className={cn(
                  'text-[15px] font-bold flex gap-2',
                  item.isActive
                    ? ' font-bold text-neutral-1'
                    : 'body-15-regular'
                )}
              >
                {item.label}
                {item.disabled && (
                  <LockIcon
                    className="text-neutral-4 bg-transparent"
                    suppressHydrationWarning
                  />
                )}
              </div>
            </a>
          </Link>
        );
      })}
    </div>
  );
};

const SidebarFooterLegacy = ({
  children,
  className = '',
  onClick,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cn(
        'px-4 py-3 flex items-center gap-2 border-t border-t-stroke-3 cursor-pointer',
        className
      )}
      {...(onClick ? { onClick } : {})}
    >
      {children}
    </div>
  );
};

export {
  SidebarBodyLegacy,
  SidebarFooterLegacy,
  SidebarHeaderLegacy,
  SidebarLegacy,
  SidebarLinksLegacy,
};
export type { SidebarLinkLegacyProps };
